import { forwardRef, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Maximize2 } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'
import { Tilt } from '../celebration'
import TemplateThumb from '../invite/TemplateThumb'
import { DESIGNED_TEMPLATES, getTemplate } from '../../lib/inviteTemplates'

// Eight painted templates that between them cover the four looks and most
// categories, as a teaser for the full gallery at /templates. The four
// animated example invitations now play in the hero.
const TEASER_IDS = [
  'wc-dino-jungle',
  'bot-magnolia',
  'photo-wine-night',
  'line-tea-party',
  'wc-over-the-moon',
  'wc-rtveli-supra',
  'photo-disco-night',
  'wc-unicorn-dreams',
]
const TEASER = TEASER_IDS.map((id) => getTemplate(id)).filter((tpl) => tpl.id !== 'auto')

// Section element is the scroll target for the hero's "See examples" CTA.
const Showcase = forwardRef<HTMLElement>((_props, ref) => {
  const { language } = useLanguage()
  const isKa = language === 'ka'
  const lang = isKa ? 'ka' : 'en'
  const reduce = useReducedMotion()

  const gridRef = useRef<HTMLDivElement>(null)
  const [degraded, setDegraded] = useState(false) // observer dead → skip entrance animation

  // Some embedded webviews paint the page while document.visibilityState is
  // "hidden" — there IntersectionObserver callbacks and rAF never run, so a
  // whileInView entrance keeps every card at opacity 0 forever while the
  // section background (plain DOM) shows fine. Watch the grid with our own
  // observer; until it proves alive, poll the grid's position and fall back
  // to showing the cards without the entrance animation.
  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    let reported = false
    let io: IntersectionObserver | undefined
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        () => {
          reported = true
        },
        { rootMargin: '0px 0px 200px 0px' },
      )
      io.observe(el)
    }
    const poll = setInterval(() => {
      if (reported) {
        clearInterval(poll)
        return
      }
      const r = el.getBoundingClientRect()
      if (r.top < window.innerHeight + 200 && r.bottom > -200) {
        setDegraded(true)
        clearInterval(poll)
      }
    }, 700)
    return () => {
      io?.disconnect()
      clearInterval(poll)
    }
  }, [])

  return (
    // Mockup .preview band: soft cream→pink-lavender vertical wash.
    <section
      ref={ref}
      className="relative scroll-mt-20 overflow-hidden bg-[linear-gradient(180deg,#fff7f2_0%,#fbeefb_100%)] dark:bg-[linear-gradient(180deg,#130b26_0%,#1d1240_100%)]"
    >
      {/* Mockup .preview blob — pink glow at the band's bottom-left. */}
      <div
        aria-hidden="true"
        className="blob blob-pink blob-drift-slow"
        style={{ width: 420, height: 420, bottom: -140, left: -140, opacity: 0.35 }}
      />
      <div className="relative mx-auto max-w-6xl px-5 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-[48ch]">
            <h2 className="font-lynx text-[clamp(1.9rem,3.5vw,2.75rem)] font-semibold tracking-tight">
              {isKa ? 'დაათვალიერე მოსაწვევების ნიმუშები' : 'Browse invitation examples'}
            </h2>
            <p className="mt-3 text-lg text-celebration-ink-2 dark:text-[#c3b8dd]">
              {isKa
                ? 'აირჩიე რამდენიმე შენთვის შექმნილი მრავალი ვერსიიდან.'
                : 'Pick a few from the many versions made for you.'}
            </p>
          </div>
          <Link
            to="/templates"
            className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-celebration-ink shadow-celebrate-sm transition-shadow hover:shadow-celebrate-md dark:bg-white/10 dark:text-[#f4f0ff]"
            data-testid="showcase-all-templates"
          >
            {isKa ? `ყველა ${DESIGNED_TEMPLATES.length} შაბლონი` : `All ${DESIGNED_TEMPLATES.length} templates`}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div ref={gridRef} className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {TEASER.map((tpl, i) => (
            <motion.div
              key={degraded ? `${tpl.id}-plain` : tpl.id}
              initial={reduce || degraded ? false : { opacity: 0, y: 46, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ type: 'spring', stiffness: 220, damping: 18, delay: (i % 4) * 0.12 }}
              className="group"
            >
              <Tilt max={8}>
                <a
                  href={`/templates/${tpl.id}?noIntro=1&lang=${lang}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block overflow-hidden rounded-bubble shadow-celebrate-sm transition-shadow duration-300 hover:shadow-celebrate-md"
                  aria-label={isKa ? `${tpl.label.ka} — გადახედვა` : `${tpl.label.en} — preview`}
                  data-testid={`showcase-template-${tpl.id}`}
                >
                  <TemplateThumb template={tpl} lang={lang} className="w-full !rounded-none" />
                  <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-celebration-ink/70 to-transparent p-3 text-white">
                    <span className="truncate text-xs font-semibold">{isKa ? tpl.label.ka : tpl.label.en}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-1 text-[10px] font-medium backdrop-blur">
                      <Maximize2 className="h-3 w-3" />
                      {isKa ? 'გადახედვა' : 'Preview'}
                    </span>
                  </span>
                </a>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
})

Showcase.displayName = 'Showcase'

export default Showcase
